import { createSlice } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import { getAuthenticatedLandingEvents } from '../util/api';

const RECOMMENDATIONS_TTL = 15 * 60 * 1000;

let inflightRequest = null;
let inflightUserId = null;

const initialState = {
  data: null,
  hasPreferences: true,
  fetchInProgress: false,
  fetchError: null,
  fetchedForUserId: null,
  lastFetched: 0,
};

const eventRecommendationsSlice = createSlice({
  name: 'eventRecommendations',
  initialState,
  reducers: {
    fetchRecommendationsRequest(state, action) {
      state.fetchInProgress = true;
      state.fetchError = null;
      if (action.payload?.userId && action.payload.userId !== state.fetchedForUserId) {
        state.data = null;
        state.hasPreferences = true;
      }
    },
    fetchRecommendationsSuccess(state, action) {
      const { data, hasPreferences, userId, fetchedAt } = action.payload;
      state.data = data;
      state.hasPreferences = hasPreferences;
      state.fetchInProgress = false;
      state.fetchError = null;
      state.fetchedForUserId = userId;
      state.lastFetched = fetchedAt;
    },
    fetchRecommendationsError(state, action) {
      state.fetchInProgress = false;
      state.fetchError = action.payload;
    },
    clearRecommendations(state) {
      state.data = null;
      state.hasPreferences = true;
      state.fetchInProgress = false;
      state.fetchError = null;
      state.fetchedForUserId = null;
      state.lastFetched = 0;
    },
  },
});

export const {
  fetchRecommendationsRequest,
  fetchRecommendationsSuccess,
  fetchRecommendationsError,
  clearRecommendations,
} = eventRecommendationsSlice.actions;

export const shouldFetchAuthenticatedRecommendations = (state, userId, options = {}) => {
  const { force = false } = options;
  const recommendationsState = state.eventRecommendations || initialState;

  if (!userId) {
    return false;
  }

  if (force) {
    return true;
  }

  if (recommendationsState.fetchInProgress && recommendationsState.fetchedForUserId === userId) {
    return false;
  }

  if (recommendationsState.fetchedForUserId !== userId) {
    return true;
  }

  if (!recommendationsState.lastFetched) {
    return true;
  }

  return Date.now() - recommendationsState.lastFetched > RECOMMENDATIONS_TTL;
};

export const fetchAuthenticatedLandingRecommendations = (options = {}) => (dispatch, getState) => {
  const state = getState();
  const userId = state.user?.currentUser?.id?.uuid;
  const isAuthenticated = state.auth?.isAuthenticated;

  if (!isAuthenticated || !userId) {
    dispatch(clearRecommendations());
    return Promise.resolve(null);
  }

  if (!shouldFetchAuthenticatedRecommendations(state, userId, options)) {
    return Promise.resolve(state.eventRecommendations?.data || null);
  }

  if (inflightRequest && inflightUserId === userId && !options.force) {
    return inflightRequest;
  }

  dispatch(fetchRecommendationsRequest({ userId }));

  inflightUserId = userId;
  inflightRequest = getAuthenticatedLandingEvents({
    limit: 6,
  })
    .then(response => {
      const hasPreferences = !!response?.hasPreferences;
      dispatch(
        fetchRecommendationsSuccess({
          data: hasPreferences ? response : null,
          hasPreferences,
          userId,
          fetchedAt: Date.now(),
        })
      );
      return response;
    })
    .catch(error => {
      dispatch(fetchRecommendationsError(storableError(error)));
      throw error;
    })
    .finally(() => {
      inflightRequest = null;
      inflightUserId = null;
    });

  return inflightRequest;
};

export default eventRecommendationsSlice.reducer;
